import React from 'react';
import {
  Box,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  Paper,
} from '@mui/material';
import {
  ErrorBox,
  ErrorTitle,
  ErrorMessage,
} from '@marageti/z4-lib';
import { FieldValues } from 'react-hook-form';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import SetPasswordForm from '../../components/set-password-form';
import SendResetLinkForm from '../../components/auth/email-reset-link-form';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso-logo-redesign.svg';
import './verification.scss';

export interface SetPasswordProps {
  email: string;
  loginError?: ApiError;
  newLinkSent: boolean;
  onPasswordSubmit: (data: FieldValues) => void;
  onResendLinkSuccess: () => void;
  tokenExpired: boolean;
}

const SetPasswordContent = ({
  email,
  loginError = undefined,
  newLinkSent,
  onPasswordSubmit,
  onResendLinkSuccess,
  tokenExpired,
}: SetPasswordProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Stack
      spacing={3}
      sx={{
        width: isDesktop ? '100%' : '342px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Stack flexDirection="column" spacing={4} alignItems="center">
        <Stack spacing={4} alignItems="center">
          <ZicassoSVG aria-label="Zicasso" />
          {!tokenExpired && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ pb: 2 }}>
              Set Your Password
            </Typography>
            <Typography color="text.secondary" style={{ textAlign: 'center' }}>
              Create a password to access Zicasso platform, contact specialists, and manage your trips.
            </Typography>
          </Box>
          )}
          {(tokenExpired && !newLinkSent) && (
          <>
            <Typography variant="h2">
              Oh, this form has expired!
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              This authentication form has expired for security reasons. Please resend and try again.
            </Typography>
            <Typography color="text.secondary">
              Email:
              {' '}
              <strong>{email}</strong>
            </Typography>
          </>
          )}
        </Stack>
        {!tokenExpired && (
        <SetPasswordForm email={email} onSubmit={onPasswordSubmit} />
        )}
        {(tokenExpired && !newLinkSent) && (
        <SendResetLinkForm email={email} onSuccess={onResendLinkSuccess} type="activate" />
        )}
      </Stack>
      {loginError && (
      <ErrorBox>
        <ErrorTitle>
          There was an on our end.
        </ErrorTitle>
        {loginError.code ? (
          <ErrorMessage>
            {loginError.code && `Code: ${loginError.code} `}
            {loginError.message || 'Please contact support'}
            <br />
            {loginError.id && `ID: ${loginError.id}`}
          </ErrorMessage>
        ) : (
          <ErrorMessage>
            Please contact support.
          </ErrorMessage>
        )}
      </ErrorBox>
      )}
    </Stack>
  );
};

const SetPasswordView = ({
  email,
  loginError = undefined,
  newLinkSent,
  onPasswordSubmit,
  onResendLinkSuccess,
  tokenExpired,
}: SetPasswordProps): React.ReactElement => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: isDesktop ? 'flex-start' : 'center',
        justifyContent: 'center',
        py: { mobile: 2, md: 4 },
        bgcolor: isDesktop ? '#f5f5f5' : 'transparent',
      }}
    >
      {isDesktop ? (
        <Dialog
          open
          aria-labelledby="verification-modal-title"
          aria-describedby="verification-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              maxWidth: '390px',
              maxHeight: '95vh',
              overflowY: 'auto',
              borderRadius: '16px',
              position: 'relative',
              zIndex: theme.zIndex.modal,
            }}
          >
            <SetPasswordContent
              email={email}
              loginError={loginError}
              newLinkSent={newLinkSent}
              onPasswordSubmit={onPasswordSubmit}
              onResendLinkSuccess={onResendLinkSuccess}
              tokenExpired={tokenExpired}
            />
          </Paper>
        </Dialog>
      ) : (
        <SetPasswordContent
          email={email}
          loginError={loginError}
          newLinkSent={newLinkSent}
          onPasswordSubmit={onPasswordSubmit}
          onResendLinkSuccess={onResendLinkSuccess}
          tokenExpired={tokenExpired}
        />
      )}
    </Box>
  );
};

export default SetPasswordView;
